/**
 * #363 — a group's site map (super admins; the Groups page shows the button to superusers only and the API refuses
 * everyone else): the drawing members can switch to in the dashboard's map window, and who may place the pins on it.
 *
 * The current drawing (or an empty state), Upload / Replace drawing (PNG or JPEG up to 10 MB, checked here before any
 * request; the server checks the type by decoding and each side up to 8000 px), "Group members can arrange pins"
 * (off by default; super admins can always arrange), Remove drawing behind a confirm inside the dialog, and Save:
 * with a new file it uploads (the switch goes with it), otherwise it changes only the switch. Pins keep their
 * positions when the drawing is replaced. Modelled on GroupCriticalDialog.
 */
import * as Dialog from '@radix-ui/react-dialog'
import * as Switch from '@radix-ui/react-switch'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2, Map as MapIcon, Trash2, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { sitePlansApi } from '@/api/endpoints'
import { queryKeys } from '@/config/queryConfig'
import { sitePlanError, useSitePlan } from '@/hooks/useSitePlan'
import { toast } from '@/lib/toast'
import type { CameraGroup } from '@/types'

interface Props {
  group: CameraGroup
  open: boolean
  onOpenChange: (open: boolean) => void
}

/** CP 363: PNG or JPEG, at most 10 MB (the API's own limits; it checks again). */
export const SITE_PLAN_MAX_BYTES = 10 * 1024 * 1024
const ACCEPT = 'image/png,image/jpeg'

/** Why a chosen file cannot be a drawing, as a locale key; null = it can. An empty type (some systems) goes by name. */
function refusal(file: File): 'wrongType' | 'tooBig' | null {
  const type = file.type || (/\.png$/i.test(file.name) ? 'image/png' : /\.jpe?g$/i.test(file.name) ? 'image/jpeg' : '')
  if (type !== 'image/png' && type !== 'image/jpeg') return 'wrongType'
  if (file.size > SITE_PLAN_MAX_BYTES) return 'tooBig'
  return null
}

export function GroupSitePlanDialog({ group, open, onOpenChange }: Props) {
  const { t } = useTranslation('groups')
  const { t: tc } = useTranslation('common')
  const qc = useQueryClient()
  const planQuery = useSitePlan(group.id, open)
  const plan = planQuery.data
  const loaded = planQuery.isSuccess
  // The user's choices; null = still what the server has.
  const [file, setFile] = useState<File | null>(null)
  const [fileError, setFileError] = useState<'wrongType' | 'tooBig' | null>(null)
  const [membersChoice, setMembersChoice] = useState<boolean | null>(null)
  const [confirmRemove, setConfirmRemove] = useState(false)
  // Bumped on Remove: a fresh file input, so the same file can be chosen again (the old input still holds it, and
  // choosing it again would fire no change — review M5)
  const [inputKey, setInputKey] = useState(0)
  const members = membersChoice ?? plan?.members_can_arrange ?? false
  const membersChanged = !!plan && members !== plan.members_can_arrange

  const refresh = () => qc.invalidateQueries({ queryKey: queryKeys.sitePlans.all() })
  const save = useMutation({
    mutationFn: () => (file ? sitePlansApi.upload(group.id, file, members) : sitePlansApi.patch(group.id, { members_can_arrange: members })),
    onSuccess: () => {
      toast.success(t('sitePlan.saved', { name: group.name }))
      void refresh()
      onOpenChange(false)
    },
    onError: (error) => {
      const reason = sitePlanError(error)
      if (reason) toast.error(t('sitePlan.failed'), reason)
      else toast.error(t('sitePlan.failed'))
    },
  })
  const remove = useMutation({
    mutationFn: () => sitePlansApi.remove(group.id),
    onSuccess: () => {
      toast.success(t('sitePlan.removed', { name: group.name }))
      setConfirmRemove(false); setFile(null); setFileError(null); setMembersChoice(null); setInputKey((n) => n + 1)
      void refresh()
    },
    onError: (error) => {
      const reason = sitePlanError(error)
      if (reason) toast.error(t('sitePlan.removeFailed'), reason)
      else toast.error(t('sitePlan.removeFailed'))
    },
  })
  const busy = save.isPending || remove.isPending
  const canSave = loaded && !busy && !fileError && (!!file || membersChanged)

  const choose = (chosen: File | null) => {
    if (!chosen) { setFile(null); setFileError(null); return }
    const why = refusal(chosen)
    setFileError(why)
    setFile(why ? null : chosen)
  }

  return (
    <Dialog.Root open={open} onOpenChange={busy ? undefined : onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-xl border border-border bg-background p-6 shadow-2xl">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <Dialog.Title className="text-lg font-semibold">{t('sitePlan.title', { name: group.name })}</Dialog.Title>
              <Dialog.Description className="text-sm text-muted-foreground">{t('sitePlan.description')}</Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button type="button" aria-label={tc('actions.close')} disabled={busy} className="rounded-md p-1 text-muted-foreground hover:bg-muted">
                <X className="h-4 w-4" />
              </button>
            </Dialog.Close>
          </div>

          {planQuery.isError ? (
            <p className="mt-4 text-sm text-destructive">{t('sitePlan.loadFailed')}</p>
          ) : !loaded ? (
            <p className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />{t('sitePlan.loading')}
            </p>
          ) : (
            <>
              {/* The drawing as it is now, or the empty state */}
              <div className="mt-4">
                {plan ? (
                  <figure className="m-0 space-y-1">
                    <div className="overflow-hidden rounded-lg border border-border bg-white">
                      <img src={plan.image_url} alt={t('sitePlan.current')} className="block max-h-64 w-full object-contain" />
                    </div>
                    <figcaption className="text-xs text-muted-foreground">{t('sitePlan.size', { width: plan.width, height: plan.height })}</figcaption>
                  </figure>
                ) : (
                  <div className="flex h-32 flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border text-center">
                    <MapIcon className="h-6 w-6 text-muted-foreground/40" aria-hidden />
                    <p className="m-0 text-sm text-muted-foreground">{t('sitePlan.empty')}</p>
                  </div>
                )}
              </div>

              <div className="mt-4 space-y-1">
                <label htmlFor="group-site-plan-file" className="text-sm font-medium">{plan ? t('sitePlan.replace') : t('sitePlan.upload')}</label>
                <input
                  key={inputKey}
                  id="group-site-plan-file"
                  type="file"
                  accept={ACCEPT}
                  disabled={busy}
                  onChange={(e) => choose(e.target.files?.[0] ?? null)}
                  aria-invalid={fileError ? true : undefined}
                  className="block w-full max-w-full text-xs file:mr-2 file:rounded file:border file:border-border file:bg-muted file:px-2 file:py-1 file:text-xs"
                />
                {fileError ? (
                  <p role="alert" className="m-0 text-xs text-destructive">{t(`sitePlan.${fileError}`)}</p>
                ) : file ? (
                  <p className="m-0 break-all text-xs text-muted-foreground">{t('sitePlan.chosen', { name: file.name })}</p>
                ) : null}
                <p className="m-0 text-xs text-muted-foreground">{t('sitePlan.fileHint')}</p>
              </div>

              <div className="mt-4 flex items-start justify-between gap-3">
                <div className="min-w-0 space-y-0.5">
                  <label className="text-sm font-medium" htmlFor="group-site-plan-members">{t('sitePlan.membersLabel')}</label>
                  <p className="m-0 text-xs text-muted-foreground">{t('sitePlan.membersHint')}</p>
                </div>
                <Switch.Root
                  id="group-site-plan-members"
                  checked={members}
                  onCheckedChange={setMembersChoice}
                  disabled={busy}
                  className="relative mt-0.5 inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-[state=checked]:bg-primary data-[state=unchecked]:bg-input disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Switch.Thumb className="pointer-events-none block h-4 w-4 rounded-full bg-background shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0" />
                </Switch.Root>
              </div>

              {plan && (
                <div className="mt-4 rounded-lg border border-border p-3">
                  {confirmRemove ? (
                    <div className="space-y-2" aria-live="polite">
                      <p className="m-0 text-sm">{t('sitePlan.removeConfirm')}</p>
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" variant="destructive" onClick={() => remove.mutate()} disabled={busy}>
                          {remove.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                          {t('sitePlan.removeYes')}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setConfirmRemove(false)} disabled={busy}>
                          {t('sitePlan.removeNo')}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button size="sm" variant="outline" className="text-destructive hover:text-destructive" onClick={() => setConfirmRemove(true)} disabled={busy}>
                      <Trash2 className="mr-2 h-4 w-4" aria-hidden />
                      {t('sitePlan.remove')}
                    </Button>
                  )}
                </div>
              )}
            </>
          )}

          <div className="mt-6 flex gap-3">
            <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)} disabled={busy}>
              {tc('actions.cancel')}
            </Button>
            <Button className="flex-1" onClick={() => save.mutate()} disabled={!canSave}>
              {save.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('sitePlan.save')}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
